/**
 * 基础动作学到哪了（UI2-BASIC-MOTION-ROWS-117；c84a 10-16 12:54，I 转）：在「TA 的小动作」里照实给主人看，每段一行。
 * 读口：A-2 BASIC-PACK-117 的 CharacterState.basic_motions（x-additive，可为 null）。类型来自正式生成契约，读的时候逐项核形状，
 * 形状不对就当没有（整块不出），不猜、不补。
 * - 数全照读口：卡头「已经会 X/14 个」照 first_batch，卡底一共几个照 overall；组头「会了 a/b」按读口给的段数，不在前端加减。
 * - pack_state、first_ready 是包自己的状态，不显示，也不当已经会的数（c84a：三样分开）；fallback 不给主人看（A-2 10-16 对过）。
 * - 说法（用户 10-16、I 10-17）：说 TA 在学新动作；不写准备、制作、生成。逐段三种：会了、在学、暂时做不好（不说原因）。
 * - 段名 36 段照 A-2 的 CLIP-KEYS.md（sha256 前 16 位 c7148a70c422b8a9），每段一句人话，用例逐个钉住；
 *   清单外的新段按所在类说「{类}里的一个动作」，类也不认得就说「一个新动作」，不露段名。
 */
import { n } from "./teachModel";

// Reuse the generated DTOs. The input type keeps the additive fallback field optional for older replies.
import type { BasicMotionStatus, BasicMotionCount, BasicMotionClipInput as BasicMotionClip } from "@/shared/contracts/generated";
export type { BasicMotionStatus, BasicMotionCount, BasicMotionClipInput as BasicMotionClip } from "@/shared/contracts/generated";

export interface BasicMotions {
  pack_state: string;
  first_ready: boolean;
  first_batch: BasicMotionCount;
  overall: BasicMotionCount;
  clips: BasicMotionClip[];
}

const STATUSES: ReadonlySet<string> = new Set<BasicMotionStatus>(["usable", "preparing", "unavailable"]);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const isCount = (value: unknown): value is BasicMotionCount =>
  isRecord(value) && Number.isInteger(value.usable) && Number.isInteger(value.total) && (value.usable as number) >= 0 && (value.usable as number) <= (value.total as number);
const isClip = (value: unknown): value is BasicMotionClip =>
  isRecord(value) && typeof value.clip_key === "string" && typeof value.category === "string" && typeof value.first_batch === "boolean" &&
  typeof value.status === "string" && STATUSES.has(value.status) && typeof value.live === "boolean";

/** 从角色状态里取基础动作这一栏：没有、null、形状不对都回 null（整块不出）。 */
export function readBasicMotions(state: unknown): BasicMotions | null {
  if (!isRecord(state)) return null;
  const value = state.basic_motions;
  if (!isRecord(value) || !isCount(value.first_batch) || !isCount(value.overall) || !Array.isArray(value.clips) || !value.clips.every(isClip)) return null;
  if (value.overall.total === 0) return null;
  return value as unknown as BasicMotions;
}

/** 每一类的叫法（组头）；按读口给的 category，先后照读口里段的先后 */
export const CATEGORY_TITLE: Readonly<Record<string, string>> = {
  stand: "站着",
  lie: "趴下",
  sleep: "睡觉",
  eat: "吃东西",
  drink: "喝水",
  petted: "被摸摸",
  sit: "坐下",
  walk: "走路",
  run: "跑",
  walk_depth: "迎面走、背身走",
};

/** 每段一句人话（CLIP-KEYS.md 的 36 段） */
export const CLIP_LABEL: Readonly<Record<string, string>> = {
  stand_idle: "站着",
  stand_to_lie: "从站着趴下",
  lie_idle: "趴着",
  lie_to_stand: "从趴着站起来",
  lie_to_sleep: "趴着睡着",
  sleep_idle: "睡着",
  sleep_to_lie: "睡醒",
  stand_to_eat: "低头吃东西",
  eat_idle: "吃东西",
  eat_to_stand: "吃完抬头",
  stand_to_drink: "低头喝水",
  drink_idle: "喝水",
  drink_to_stand: "喝完抬头",
  petted_stand: "站着被摸摸",
  petted_lie: "趴着被摸摸",
  stand_to_sit: "坐下",
  sit_idle: "坐着",
  sit_to_stand: "从坐着站起来",
  stand_to_walk_left: "往左起步",
  walk_loop_left: "往左走",
  walk_to_stand_left: "往左走完停下",
  stand_to_walk_right: "往右起步",
  walk_loop_right: "往右走",
  walk_to_stand_right: "往右走完停下",
  stand_to_run_left: "往左起跑",
  run_loop_left: "往左跑",
  run_to_stand_left: "往左跑完停下",
  stand_to_run_right: "往右起跑",
  run_loop_right: "往右跑",
  run_to_stand_right: "往右跑完停下",
  stand_to_walk_front: "迎面起步",
  walk_loop_front: "迎面走",
  walk_to_stand_front: "迎面走完停下",
  stand_to_walk_up: "背身起步",
  walk_loop_up: "背身走",
  walk_to_stand_up: "背身走完停下",
};

export const STATUS_LABEL: Readonly<Record<BasicMotionStatus, string>> = { usable: "会了", preparing: "在学", unavailable: "暂时做不好" };

export const BASIC_TITLE = "基础动作";
export const basicHeadLine = (count: BasicMotionCount) => `已经会 ${count.usable}/${n(count.total, "个")}`;
export const basicGroupLine = (usable: number, total: number) => `会了 ${usable}/${total}`;
export const basicOverallLine = (count: BasicMotionCount) => `TA 的基础动作一共 ${n(count.total, "个")}，已经会 ${n(count.usable, "个")}`;
export const basicUnavailableLine = (count: number) => `有 ${n(count, "个")}动作 TA 暂时做不好`;
export const BASIC_TRANSITION = "换了新样子，有几个动作 TA 还在照新样子学，先用原来的样子做";
export const basicLearningRow = (count: number) => `TA 还在学 ${n(count, "个")}基础动作`;

export function clipLabel(clip: Pick<BasicMotionClip, "clip_key" | "category">): string {
  const label = CLIP_LABEL[clip.clip_key];
  if (label) return label;
  const title = CATEGORY_TITLE[clip.category];
  return title ? `${title}里的一个动作` : "一个新动作";
}

export function categoryTitle(category: string): string {
  return CATEGORY_TITLE[category] ?? "别的动作";
}

export interface BasicMotionGroup {
  category: string;
  title: string;
  usable: number;
  total: number;
  learning: number;
  clips: BasicMotionClip[];
}

/** 按类分组（先后照读口里段的先后），组头的数照这一组读口给的段 */
export function groupBasicMotions(clips: readonly BasicMotionClip[]): BasicMotionGroup[] {
  const groups = new Map<string, BasicMotionGroup>();
  for (const clip of clips) {
    const group = groups.get(clip.category) ?? { category: clip.category, title: categoryTitle(clip.category), usable: 0, total: 0, learning: 0, clips: [] };
    group.total += 1;
    if (clip.status === "usable") group.usable += 1;
    if (clip.status === "preparing") group.learning += 1;
    group.clips.push(clip);
    groups.set(clip.category, group);
  }
  return [...groups.values()];
}

/** 还在学几段（status＝preparing） */
export const learningCount = (motions: BasicMotions) => motions.clips.filter((clip) => clip.status === "preparing").length;
/** 暂时做不好几段 */
export const unavailableCount = (motions: BasicMotions) => motions.clips.filter((clip) => clip.status === "unavailable").length;
/** 换形象过渡期：有会了的段还没按新样子生效（家里照播原来的样子） */
export const inTransition = (motions: BasicMotions) => motions.clips.some((clip) => clip.status === "usable" && !clip.live);
